import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import eyespyImg from '../media/software/eyespy/glasses.png';
import azlonImg from '../media/software/azlon/harrison_presentation_title.png';
import joyrideImg from '../media/software/joyride/gt3rs.jpeg';
import ezjobImg from '../media/software/ezjob/airport_lounge.jpg';
import discordImg from '../media/software/discord/devin.jpg';
import atradeImg from '../media/software/atrade/desk_stock.jpg';

const projects = [
  {
    id: 1,
    name: "EyeSpy",
    category: "Agentic AI",
    backgroundImage: eyespyImg,
  },
  {
    id: 2,
    name: "ATrade",
    category: "FinTech / Agentic AI",
    backgroundImage: atradeImg,
  },
  {
    id: 3,
    name: "Azlon",
    category: "AI / ML",
    backgroundImage: azlonImg,
  },
  {
    id: 4,
    name: "JoyRide",
    category: "IOS Application",
    backgroundImage: joyrideImg,
  },
  {
    id: 5,
    name: "EzJob",
    category: "Website",
    backgroundImage: ezjobImg,
  },
  {
    id: 6,
    name: "Discord Trading Bot",
    category: "Scripting",
    backgroundImage: discordImg,
  }
];

const SoftwareRow = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const location = useLocation();
  const scrollRef = useRef(null);
  const [showArrows, setShowArrows] = useState({ left: false, right: false });

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowArrows({
        left: scrollLeft > 10,
        right: scrollLeft < scrollWidth - clientWidth - 10
      });
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="software" className="py-8 bg-black">
      <div className="px-8 md:px-12 lg:px-16">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
          Software Development
        </h2>

        <div className="relative group">
          {/* Left Arrow */}
          {showArrows.left && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-40 group"
              aria-label="Scroll left"
            >
              <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </div>
            </button>
          )}

          {/* Right Arrow */}
          {showArrows.right && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-40 group"
              aria-label="Scroll right"
            >
              <div className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 backdrop-blur-md border border-white/20 transition-all duration-300 group-hover:bg-white/20 group-hover:scale-110">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          )}

          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {projects.map((project) => (
              <Link
                key={project.id}
                to={`/software/${project.name.toLowerCase().replace(/\s+/g, '-')}`}
                state={{ background: location }}
                className="flex-shrink-0 w-[280px] md:w-[320px] relative group/card cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-lg"
                onMouseEnter={() => setHoveredCard(project.id)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div
                  className={`relative h-full rounded-lg overflow-hidden transition-all duration-300 ${hoveredCard === project.id ? 'transform scale-105 z-20 shadow-2xl shadow-black/50' : 'z-10'
                    }`}
                >
                  {project.backgroundImage && (
                    <div
                      className="absolute inset-0 w-full h-full"
                      style={{
                        background: `url(${project.backgroundImage}) center/cover`,
                      }}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />

                  <div className="relative p-6 h-full flex flex-col justify-end min-h-[450px]">
                    <div>
                      <span className="inline-block bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded mb-3">
                        {project.category}
                      </span>

                      <div className="flex items-center gap-4 mb-2">
                        <h3 className="text-2xl md:text-3xl font-bold text-white">
                          {project.name}
                        </h3>

                        <span
                          className={`bg-white text-black py-2 px-4 rounded-md font-semibold transition-all duration-300 flex-shrink-0 text-sm ${hoveredCard === project.id ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
                            }`}
                        >
                          Learn More
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SoftwareRow;
